/**
 * useIntegrations - Hook for managing Composio integrations
 *
 * Handles:
 * - Fetching available integrations catalog
 * - User's connected integrations
 * - OAuth connection flow
 * - Disconnection
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { resilientFetch } from "@/lib/fetch";
import { posthog } from "@/lib/posthog";
import { captureV1 } from "@/lib/analytics-v1";
import { ANALYTICS_EVENTS } from "@bash/shared";
import type { ConnectedAccountWire } from "@bash/shared";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

// =============================================================================
// Types
// =============================================================================

/** A catalog row as the server sends it (composio.service ToolkitInfo), plus
 *  the legacy spellings older payloads carried. mapItem folds it into Integration. */
interface CatalogWireItem {
  name: string;
  slug: string;
  description: string;
  logoUrl?: string;
  logo?: string;
  category?: string | null;
  categories?: string[];
  authMode?: string;
  toolCount?: number;
  toolsCount?: number;
  actionsCount?: number;
  tools_count?: number;
  connectable?: boolean;
  noAuth?: boolean;
}

export interface Integration {
  name: string;
  slug: string;
  description: string;
  logo?: string;
  categories?: string[];
  authMode?: string;
  toolsCount?: number;
  /** Connect works today (Composio-managed auth or our own auth config). Absent
   *  on an older server or the decoration path, and absent reads as true: never
   *  grey an app on a guess. */
  connectable?: boolean;
  /** No sign-in at all (Composio Search, Hacker News): nothing to connect.
   *  Absent reads as false, so no app loses its Connect on a guess. */
  noAuth?: boolean;
}

export interface UserConnection {
  toolkit_slug: string;
  status: "active" | "pending" | "expired" | "error";
  connection_id: string | null;
  account_name: string | null;
  /** Catalog identity, resolved server-side from the full catalog — null when
   *  the catalog has no entry for the slug (the row still renders; a live
   *  grant must never vanish from the user's inventory). */
  name?: string | null;
  description?: string | null;
  logo_url?: string | null;
  tool_count?: number | null;
  /** Present ONLY when the server runs MULTI_ACCOUNT_V1: every account for
   *  this toolkit, server-ordered ACTIVE first then newest (accounts[0] is
   *  the primary/winner). Absent ⇒ single-account wire; render exactly
   *  today's UI (the flag-off axis must stay byte-identical). */
  accounts?: ConnectedAccountWire[];
}

/** The server's structured connect-failure message when it sent one (e.g. the
 *  422 toolkit_not_connectable body), else the bare status — the raw "Failed
 *  to initiate connection: 500" is what a user screenshotted into support. */
export function connectFailureMessage(status: number, body: unknown): string {
  const m = (body as { message?: unknown } | null)?.message;
  if (typeof m === "string" && m.trim().length > 0) return m;
  return `Failed to initiate connection: ${status}`;
}

/** A connect the server refused, carrying its structured reason when it sent
 *  one (`toolkit_not_connectable` on the 422) so callers branch on the code,
 *  never on the message text. */
export class ConnectFailedError extends Error {
  readonly status: number;
  readonly code: string | null;
  constructor(status: number, body: unknown) {
    super(connectFailureMessage(status, body));
    this.name = "ConnectFailedError";
    this.status = status;
    const code = (body as { error?: unknown } | null)?.error;
    this.code = typeof code === "string" ? code : null;
  }
}

/** ≥1 account of ANY status on the grouped wire; the legacy wire keeps the
 *  active-only judgement byte-identical. */
export function hasAnyAccount(conn: UserConnection | undefined): boolean {
  if (!conn) return false;
  return conn.accounts ? conn.accounts.length > 0 : conn.status === "active";
}

export interface IntegrationsState {
  /** Available integrations from Composio catalog */
  catalog: Integration[];
  /** User's connected integrations */
  connections: Map<string, UserConnection>;
  /** Loading states */
  catalogLoading: boolean;
  connectionsLoading: boolean;
  /** Loading more (pagination) */
  loadingMore: boolean;
  /** Error states */
  catalogError: string | null;
  connectionsError: string | null;
  /** Connection in progress */
  connectingTo: string | null;
  /** Disconnection in progress */
  disconnectingSlug: string | null;
  /** Per-account disconnect in flight (grouped wire only). */
  disconnectingAccountId: string | null;
  /** Pagination - offset-based */
  currentOffset: number;
  hasMore: boolean;
  total: number;
  /** Current search query */
  searchQuery: string;
}

interface CatalogResponse {
  items: CatalogWireItem[];
  total: number;
  nextCursor?: string;
  hasMore: boolean;
}

interface ConnectionsResponse {
  connections: UserConnection[];
}

interface ConnectResponse {
  integration_name: string;
  auth_mode: string;
  auth_url: string | null;
  request_id: string | null;
  is_complete: boolean;
  connection_id: string | null;
  message?: string;
}

interface VerifyResponse {
  success: boolean;
  connected: boolean;
  status: string;
  connection_id: string | null;
  account_name: string | null;
}

// =============================================================================
// Hook
// =============================================================================

// Session-scoped caches so the Apps page renders instantly on re-entry instead
// of re-flashing the 12-card skeleton; the mount effects revalidate silently
// (stale-while-revalidate). Module-level is safe — sign-out does a full reload
// (AuthContext) which resets these, so a different user can't inherit them.
// Only the DEFAULT (no-search) catalog is cached, so a nav-in always shows the
// default list rather than a stale search result.
let _catalogCache: {
  catalog: Integration[];
  currentOffset: number;
  hasMore: boolean;
  total: number;
} | null = null;
let _connectionsCache: Map<string, UserConnection> | null = null;

/** Test-only: clear the session caches so each test starts from a cold state. */
export function __resetIntegrationsCacheForTests() {
  _catalogCache = null;
  _connectionsCache = null;
}

export function useIntegrations(options?: { autoLoad?: boolean }) {
  const { session } = useAuth();
  // Consumers that only need an action (e.g. home uses `connect` for OAuth)
  // pass autoLoad:false so they don't pay for the catalog/connections fetches
  // on mount. The Apps page leaves it default-true.
  const autoLoad = options?.autoLoad ?? true;

  const [state, setState] = useState<IntegrationsState>({
    catalog: _catalogCache?.catalog ?? [],
    connections: _connectionsCache ?? new Map(),
    // Only show a loader when there's nothing cached to render.
    catalogLoading: _catalogCache === null,
    connectionsLoading: _connectionsCache === null,
    loadingMore: false,
    catalogError: null,
    connectionsError: null,
    connectingTo: null,
    disconnectingSlug: null,
    disconnectingAccountId: null,
    currentOffset: _catalogCache?.currentOffset ?? 0,
    hasMore: _catalogCache?.hasMore ?? false,
    total: _catalogCache?.total ?? 0,
    searchQuery: "",
  });

  // Helper for authenticated requests
  const fetchWithAuth = useCallback(
    async (url: string, options: RequestInit = {}) => {
      const headers: Record<string, string> = {};

      // Only set Content-Type for requests with a body
      // Fastify rejects empty bodies with application/json content-type
      if (options.body) {
        headers["Content-Type"] = "application/json";
      }

      // Merge any existing headers
      if (options.headers) {
        const existingHeaders = options.headers as Record<string, string>;
        Object.assign(headers, existingHeaders);
      }

      if (session?.access_token) {
        headers["Authorization"] = `Bearer ${session.access_token}`;
      }

      return resilientFetch(url, {
        ...options,
        headers,
      });
    },
    [session?.access_token]
  );

  // Map backend item to frontend Integration type
  const mapItem = (item: CatalogWireItem): Integration => ({
    name: item.name,
    slug: item.slug,
    description: item.description,
    logo: item.logoUrl || item.logo,
    categories: item.category ? [item.category] : item.categories || [],
    authMode: item.authMode,
    toolsCount:
      item.toolCount ||
      item.toolsCount ||
      item.actionsCount ||
      item.tools_count,
    connectable: item.connectable ?? true,
    noAuth: item.noAuth ?? false,
  });

  // Fetch catalog with optional search and pagination (offset-based)
  const fetchCatalog = useCallback(
    async (options?: {
      search?: string;
      offset?: number;
      append?: boolean;
    }) => {
      const isNewSearch =
        options?.search !== undefined && options.search !== state.searchQuery;
      const isLoadMore =
        options?.append && options?.offset !== undefined && options.offset > 0;
      const isInitialLoad = !options?.search && !options?.append;

      setState((s) => ({
        ...s,
        // Keep the cached list visible on a silent revalidate (initial load with a
        // warm cache); only show the skeleton when there's nothing to render.
        catalogLoading:
          !isLoadMore && !(isInitialLoad && _catalogCache !== null),
        loadingMore: !!isLoadMore,
        catalogError: null,
        searchQuery: options?.search ?? s.searchQuery,
      }));

      try {
        const params = new URLSearchParams();
        params.set("limit", "30"); // Load 30 items per page for infinite scroll

        if (options?.search) {
          params.set("search", options.search);
        }

        // Use offset for pagination (reset to 0 for new searches)
        const offset = isNewSearch ? 0 : (options?.offset ?? 0);
        params.set("offset", String(offset));

        const response = await resilientFetch(
          `${SERVER_URL}/api/integrations/catalog?${params}`
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch catalog: ${response.status}`);
        }
        const data: CatalogResponse = await response.json();

        const items: Integration[] = (data.items || []).map(mapItem);
        const nextOffset = offset + items.length;

        // Cache only the DEFAULT list (initial load, no search, no append) so a
        // nav-in re-renders it instantly and never shows a stale search result.
        if (isInitialLoad) {
          _catalogCache = {
            catalog: items,
            currentOffset: nextOffset,
            hasMore: data.hasMore,
            total: data.total,
          };
        }

        setState((s) => ({
          ...s,
          catalog:
            options?.append && !isNewSearch ? [...s.catalog, ...items] : items,
          catalogLoading: false,
          loadingMore: false,
          currentOffset: nextOffset,
          hasMore: data.hasMore,
          total: data.total,
        }));
      } catch (error) {
        console.error("Failed to fetch integrations catalog:", error);
        setState((s) => ({
          ...s,
          catalogLoading: false,
          loadingMore: false,
          catalogError:
            error instanceof Error ? error.message : "Unknown error",
        }));
      }
    },
    [state.searchQuery]
  );

  // Load more items (offset-based pagination)
  const loadMore = useCallback(() => {
    if (state.loadingMore || !state.hasMore) return;
    fetchCatalog({
      offset: state.currentOffset,
      append: true,
      search: state.searchQuery || undefined,
    });
  }, [
    state.loadingMore,
    state.hasMore,
    state.currentOffset,
    state.searchQuery,
    fetchCatalog,
  ]);

  // Search with the given query (server-side)
  const search = useCallback(
    (query: string) => {
      fetchCatalog({ search: query });
    },
    [fetchCatalog]
  );

  // Fetch user connections (requires auth)
  const fetchConnections = useCallback(async () => {
    if (!session?.access_token) {
      setState((s) => ({ ...s, connectionsLoading: false }));
      return;
    }

    // Silent revalidate when we already have a cached connections map.
    setState((s) => ({
      ...s,
      connectionsLoading: _connectionsCache === null,
      connectionsError: null,
    }));
    try {
      const response = await fetchWithAuth(
        `${SERVER_URL}/api/integrations/my-connections`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch connections: ${response.status}`);
      }
      const data: ConnectionsResponse = await response.json();
      const connectionsMap = new Map<string, UserConnection>();
      data.connections.forEach((conn) => {
        connectionsMap.set(conn.toolkit_slug, conn);
      });
      _connectionsCache = connectionsMap;
      setState((s) => ({
        ...s,
        connections: connectionsMap,
        connectionsLoading: false,
      }));
    } catch (error) {
      console.error("Failed to fetch user connections:", error);
      setState((s) => ({
        ...s,
        connectionsLoading: false,
        connectionsError:
          error instanceof Error ? error.message : "Unknown error",
      }));
    }
  }, [session?.access_token, fetchWithAuth]);

  // Connect to an integration (initiates OAuth flow)
  const connect = useCallback(
    async (
      toolkitSlug: string,
      opts?: { mode?: "add" }
    ): Promise<{ authUrl?: string; isComplete?: boolean }> => {
      setState((s) => ({ ...s, connectingTo: toolkitSlug }));
      // Analytics v1: the person started connecting an app. Whether it worked
      // is the server's word (integration:connection_confirmed), not ours.
      captureV1(ANALYTICS_EVENTS.INTEGRATION_CONNECT_STARTED, {
        provider: toolkitSlug,
      });

      try {
        const response = await fetchWithAuth(
          `${SERVER_URL}/api/integrations/${toolkitSlug}/connect`,
          {
            method: "POST",
            // Body ONLY in add-mode: the plain request must stay byte-identical
            // (fetchWithAuth adds Content-Type only when a body exists, and
            // Fastify rejects empty JSON bodies).
            ...(opts?.mode === "add"
              ? { body: JSON.stringify({ mode: "add" }) }
              : {}),
          }
        );

        if (!response.ok) {
          const body: unknown = await response.json().catch(() => null);
          throw new ConnectFailedError(response.status, body);
        }

        const data: ConnectResponse = await response.json();

        if (data.is_complete) {
          // Connection completed immediately (NO_AUTH integrations)
          await fetchConnections();
          setState((s) => ({ ...s, connectingTo: null }));
          return { isComplete: true };
        }

        // Need OAuth redirect
        setState((s) => ({ ...s, connectingTo: null }));
        return { authUrl: data.auth_url || undefined };
      } catch (error) {
        console.error("Failed to connect integration:", error);
        setState((s) => ({ ...s, connectingTo: null }));
        throw error;
      }
    },
    [fetchWithAuth, fetchConnections]
  );

  // Track an OAuth popup to closure, then re-fetch connections. Polling for
  // popup.closed fires on finish AND on cancel/abandon (a cancelled OAuth
  // produces no callback). Shared by connect, add-account, and reconnect.
  // Declared here (not beside connectAndTrack) so reconnectAccountAndTrack,
  // which sits with the other account actions above it, can depend on it.
  const trackPopup = useCallback(
    (toolkitSlug: string, popup: Window) => {
      setState((s) => ({ ...s, connectingTo: toolkitSlug }));
      const releaseLatch = () =>
        setState((s) =>
          s.connectingTo === toolkitSlug ? { ...s, connectingTo: null } : s
        );

      const pollInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollInterval);
          fetchConnections().finally(releaseLatch);
        }
      }, 500);

      // Safety: stop polling after 5 minutes
      setTimeout(
        () => {
          clearInterval(pollInterval);
          releaseLatch();
        },
        5 * 60 * 1000
      );
    },
    [fetchConnections]
  );

  // Verify connection after OAuth callback
  const verifyConnection = useCallback(
    async (toolkitSlug: string): Promise<boolean> => {
      try {
        const response = await fetchWithAuth(
          `${SERVER_URL}/api/integrations/${toolkitSlug}/verify`,
          { method: "POST" }
        );

        if (!response.ok) {
          throw new Error(`Failed to verify connection: ${response.status}`);
        }

        const data: VerifyResponse = await response.json();

        if (data.connected) {
          await fetchConnections();
        }

        return data.connected;
      } catch (error) {
        console.error("Failed to verify connection:", error);
        return false;
      }
    },
    [fetchWithAuth, fetchConnections]
  );

  // Disconnect an integration — waits for both Composio and DB deletion
  const disconnect = useCallback(
    async (toolkitSlug: string): Promise<boolean> => {
      const key = toolkitSlug.toLowerCase();
      // Optimistic: remove the connection the instant the button is clicked so
      // the card flips to "not connected" immediately. Keep the session cache in
      // sync so a nav-away-and-back doesn't briefly show the removed row.
      setState((s) => {
        const newConnections = new Map(s.connections);
        newConnections.delete(key);
        _connectionsCache = newConnections;
        return {
          ...s,
          connections: newConnections,
          disconnectingSlug: toolkitSlug,
        };
      });

      try {
        const response = await fetchWithAuth(
          `${SERVER_URL}/api/integrations/${toolkitSlug}`,
          { method: "DELETE" }
        );

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(
            body.details ||
              body.error ||
              `Failed to disconnect: ${response.status}`
          );
        }

        setState((s) => ({ ...s, disconnectingSlug: null }));
        posthog.capture(ANALYTICS_EVENTS.INTEGRATION_DISCONNECTED, {
          provider: toolkitSlug,
        });
        return true;
      } catch (error) {
        console.error("Failed to disconnect integration:", error);
        setState((s) => ({ ...s, disconnectingSlug: null }));
        // The DELETE failed, so the connection is still live on the server —
        // re-sync to restore the row we optimistically removed.
        fetchConnections();
        throw error;
      }
    },
    [fetchWithAuth, fetchConnections]
  );

  // Rename one account (grouped wire only). NOT optimistic, unlike the disconnect
  // below: the server can refuse a name the client cannot predict (a sibling in
  // the same toolkit already holds it, case-insensitively), and a label that
  // appears on the coin and then silently reverts is worse than one that takes a
  // moment to land. The popover keeps the field open until this resolves, and
  // renders the rejection message in place.
  const renameAccount = useCallback(
    async (accountId: string, alias: string): Promise<void> => {
      const response = await fetchWithAuth(
        `${SERVER_URL}/api/integrations/accounts/${accountId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ alias }),
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        // Parse the structured field, never the status text: `message` is written
        // for the user, and the envelope is the server's own contract.
        throw new Error(
          body.message ||
            body.details ||
            "Couldn't save that name. Please try again."
        );
      }
      // The label is winner-derived on the toolkit row too (account_name), so a
      // re-fetch is the only way the card and the drawer agree on one string.
      await fetchConnections();
    },
    [fetchWithAuth, fetchConnections]
  );

  // Per-account disconnect (grouped wire only). Optimistic: drop the account
  // (or the whole row when it was the last) immediately; a success still
  // re-fetches because the surviving row's account_name/status are
  // winner-derived server-side; a failure re-fetches to restore.
  const disconnectAccount = useCallback(
    async (
      toolkitSlug: string,
      accountId: string
    ): Promise<{ demoteDeferred: boolean }> => {
      const key = toolkitSlug.toLowerCase();
      setState((s) => {
        const conn = s.connections.get(key);
        if (!conn?.accounts) return { ...s, disconnectingAccountId: accountId };
        const remaining = conn.accounts.filter((a) => a.id !== accountId);
        const next = new Map(s.connections);
        if (remaining.length === 0) next.delete(key);
        else next.set(key, { ...conn, accounts: remaining });
        _connectionsCache = next;
        return { ...s, connections: next, disconnectingAccountId: accountId };
      });

      try {
        const response = await fetchWithAuth(
          `${SERVER_URL}/api/integrations/accounts/${accountId}`,
          { method: "DELETE" }
        );
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(
            body.details ||
              body.error ||
              `Failed to disconnect: ${response.status}`
          );
        }
        const data = (await response.json()) as {
          success: boolean;
          toolkit: string;
          account_id: string;
          /** Present ONLY when the routine demote did not land server-side. */
          demote_deferred?: boolean;
        };
        setState((s) => ({ ...s, disconnectingAccountId: null }));
        // Same event as the toolkit-level disconnect, with an additive
        // discriminator: without it, removing ONE of three accounts and
        // severing the whole toolkit are indistinguishable in the funnel.
        posthog.capture(ANALYTICS_EVENTS.INTEGRATION_DISCONNECTED, {
          provider: toolkitSlug,
          scope: "account",
        });
        void fetchConnections();
        return { demoteDeferred: data.demote_deferred === true };
      } catch (error) {
        console.error("Failed to disconnect account:", error);
        setState((s) => ({ ...s, disconnectingAccountId: null }));
        // The DELETE failed, so the account is still live server-side —
        // re-sync to restore what we optimistically removed.
        fetchConnections();
        throw error;
      }
    },
    [fetchWithAuth, fetchConnections]
  );

  // Honest per-account reconnect. A needs-reconnect account can only be fixed
  // by NEW consent, and a plain /connect would strand the dead row and mint a
  // parallel sibling (retry-minted duplicates, design §1) — or, with an ACTIVE
  // sibling, short-circuit "Already connected" with no URL at all
  // (apps/server/src/handlers/integrations-routes.handler.ts, the connect
  // handler's already-connected branch). So: delete the dead row first, then
  // mint — add-mode when the toolkit still holds another account (add-mode
  // always mints), plain when it holds none (nothing left to short-circuit on).
  const reconnectAccountAndTrack = useCallback(
    async (
      toolkitSlug: string,
      accountId: string,
      opts?: { onDeleted?: (result: { demoteDeferred: boolean }) => void }
    ): Promise<{ demoteDeferred: boolean }> => {
      const key = toolkitSlug.toLowerCase();
      const before = state.connections.get(key)?.accounts ?? [];
      const siblingsRemain = before.some((a) => a.id !== accountId);

      const deleted = await disconnectAccount(toolkitSlug, accountId);
      // The delete has landed — report it NOW, before the mint can throw, so a
      // deferred demote is never swallowed by a failed sign-in start.
      opts?.onDeleted?.(deleted);
      const result = await connect(
        toolkitSlug,
        siblingsRemain ? { mode: "add" } : undefined
      );
      if (!result.isComplete && result.authUrl) {
        const popup = window.open(result.authUrl, "_blank");
        // Unlike a plain connect, the destructive half has ALREADY landed here,
        // so a blocked popup is not a retryable no-op — the account is gone and
        // no sign-in ever opened. Safari closes its transient-activation window
        // across the delete and mint awaits above, which makes this that
        // browser's ordinary path. Raise so the page can say so; the delete was
        // reported through onDeleted before this point, so the routines note
        // still lands.
        if (!popup) {
          throw new Error(
            "Your browser blocked the sign-in window. Use Add account to finish reconnecting."
          );
        }
        trackPopup(toolkitSlug, popup);
      }
      return deleted;
    },
    [state.connections, disconnectAccount, connect, trackPopup]
  );

  // Check if an integration is connected
  const isConnected = useCallback(
    (toolkitSlug: string): boolean => {
      return hasAnyAccount(state.connections.get(toolkitSlug.toLowerCase()));
    },
    [state.connections]
  );

  // Get connection status for an integration
  const getConnectionStatus = useCallback(
    (toolkitSlug: string): UserConnection | undefined => {
      return state.connections.get(toolkitSlug.toLowerCase());
    },
    [state.connections]
  );

  // Initial load — only when this consumer actually shows the catalog.
  useEffect(() => {
    if (autoLoad) fetchCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad]);

  useEffect(() => {
    if (autoLoad) fetchConnections();
  }, [autoLoad, fetchConnections]);

  // Connect and track the popup — polls for popup closure, then re-fetches connections.
  // Handles both OAuth (callback broadcast) and API key (popup closed manually) flows.
  // `connectingTo` stays latched for the WHOLE popup lifetime (not just the
  // initiate POST) so the card shows its connecting state while the user is
  // on the provider's page, then settles to connected-or-idle on close.
  const connectAndTrack = useCallback(
    async (toolkitSlug: string, opts?: { mode?: "add" }): Promise<void> => {
      const result = await connect(toolkitSlug, opts);
      if (result.isComplete || !result.authUrl) return;

      const popup = window.open(result.authUrl, "_blank");
      if (!popup) return; // popup blocked — caller can fallback

      trackPopup(toolkitSlug, popup);
    },
    [connect, trackPopup]
  );

  // The user's live connections as renderable catalog cards — the honest
  // "Your apps" source. Derived from connections (server truth), NEVER from
  // `catalog ∩ connected`: the catalog is paginated, so anything past the
  // loaded slice (googlesuper/posthog/granola, 2026-08-12) vanished from the
  // shelf while its grant stayed live. Missing catalog identity → fallbacks;
  // a live grant always renders. "Live" is hasAnyAccount: ACTIVE on the legacy
  // wire, ≥1 account of ANY status on the grouped one, because a toolkit whose
  // only account needs reconnecting must still show up to BE reconnected.
  const connectedApps: Integration[] = useMemo(
    () =>
      [...state.connections.values()]
        .filter((conn) => hasAnyAccount(conn))
        .map((conn) => ({
          slug: conn.toolkit_slug,
          name: conn.name || conn.toolkit_slug,
          description:
            conn.description ||
            `Connected ${conn.name || conn.toolkit_slug} account`,
          logo: conn.logo_url || undefined,
          toolsCount: conn.tool_count ?? undefined,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [state.connections]
  );

  return {
    ...state,
    connectedApps,
    fetchCatalog,
    fetchConnections,
    connect,
    connectAndTrack,
    verifyConnection,
    disconnect,
    disconnectAccount,
    renameAccount,
    reconnectAccountAndTrack,
    isConnected,
    getConnectionStatus,
    loadMore,
    search,
  };
}
